import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import * as Store from '@apply/store';
import moment from 'moment-timezone';
import { loginAlertModal, EsignNoticeModal, FloodReportEsignModal, Font, isMobileScreen } from '@apply/ui';
import { useAuthentication } from '../../../Login/hooks/useAuthentication';
import LoadPanel from 'devextreme-react/load-panel';
import { FeatureType, NoticeType } from '@apply/constants';
import { MobileNotices } from './components/MobileNotices.tsx';
import { Notices } from './components/Notices.tsx';
import { GenericAlertPopup } from '@apply/components';

export interface NoticesBaseProps {
    actions?: Actions;
    notices?: Store.UserStore.Types.UserNoticeLean[];
}

export interface Actions {
    getUserNotices?: () => Promise<void>;
}

export const formatDate = (dt, format = 'M/D/YYYY hh:mm') => {
    if (!dt || !dt._isValid) {
        return null;
    }

    const formattedDate = moment.tz(dt, 'America/Chicago');
    return formattedDate.format(format);
};

export const mapType = (type: number) => {
    switch (type) {
        case Store.UserStore.Types.NoticeType.AdverseAction:
            return 'Adverse Action';
        case Store.UserStore.Types.NoticeType.AppraisalNotice:
            return 'Appraisal Notice';
        case Store.UserStore.Types.NoticeType.AppraisalReport:
            return 'Appraisal Report';
        case Store.UserStore.Types.NoticeType.FloodNotice:
            return 'Flood Notice';
        case Store.UserStore.Types.NoticeType.IncompleteFile:
            return 'Incomplete File';
        case Store.UserStore.Types.NoticeType.PostClosingCertificationNotice:
            return 'Post Closing Cert.';
        default:
            return 'Unknown';
    }
};

const getUserNotice = async (id) => await Store.CrossStore.getUserNotice(id);

const signUserNotice = async (signNotice: Store.UserStore.Types.SignUserNotice) =>
    await Store.CrossStore.signUserNotice(signNotice);

const setUserNoticeRead = async (id: number) => await Store.CrossStore.setUserNoticeRead(id);

const uploadSignedFloodReport = async (noticeId, file) => await Store.CrossStore.uploadSignedFloodReport(noticeId, file);

export const NoticesBase: React.FC<NoticesBaseProps> = ({ actions, notices }) => {
    const { isRegisteredUser, authentication, logout, isFeatureEnabled } = useAuthentication();
    const [loadError, setLoadError] = useState<string>('');

    const [userNotice, set_userNotice] = useState<Store.UserStore.Types.UserNotice>(null);
    const [viewModalOpen, set_viewModalOpen] = useState<boolean>(false);
    const [floodReportSignOpen, set_floodReportSignOpen] = useState<boolean>(false);

    const [loadingOpen, set_loadingOpen] = useState<boolean>(false);
    const [loadingMessage] = useState<string>('Loading...');

    const allowedPageSizes = [15, 25, 50];
    const defaultPageSize = allowedPageSizes[1];

    const thisLocation = useLocation();
    const navigate = useNavigate();

    const [isMobile, setIsMobile] = useState<boolean>(false);

    useEffect(() => {
        if (authentication?.user?.userId && actions?.getUserNotices) {
            set_loadingOpen(true);
            setLoadError('');
            loadNotices();
        }
    }, [authentication?.user?.userId, actions]);

    useEffect(() => {
        setIsMobile(isMobileScreen());
    }, []);

    useEffect(() => {
        if (loadError) {
            if (String(loadError).includes('401 - User not Authorized.')) {
                loginAlertModal({
                    onConfirm: () => {
                        setLoadError(undefined);
                        logout();
                        navigate(`/login?redirectTo=${thisLocation.pathname}`);
                    },
                });
            } else {
                GenericAlertPopup({
                    title: 'Error',
                    children: 'An error has occurred while communicating with our server.  Please try your operation again.',
                    width: '350px',
                });
                setLoadError(undefined);
            }
        }
    }, [navigate, loadError, logout, thisLocation.pathname]);

    if (!isFeatureEnabled(FeatureType.Notices)) {
        window.location.href = '/home';
    }

    const loadNotices = async () => {
        await actions
            .getUserNotices()
            .then(() => {
                set_loadingOpen(false);
            })
            .catch((err) => {
                setLoadError(err);
                set_loadingOpen(false);
            });
    };

    const markAsRead = async (notice) => {
        if (notice.noticeTypeId === NoticeType.FloodNotice) {
            //Can only be marked as read if they signed it.
            return;
        }
        if (!notice.isRead) {
            await setUserNoticeRead(notice.id);
            await loadNotices();
        }
    };

    const onClickViewNotice = async (id: number) => {
        try {
            set_loadingOpen(true);
            const notice = await getUserNotice(id);
            set_userNotice(notice);
            if (notice.noticeTypeId === NoticeType.FloodNotice) {
                set_floodReportSignOpen(true);
            } else {
                if (notice.noticeFile) {
                    set_viewModalOpen(true);
                }
            }
        } finally {
            set_loadingOpen(false);
        }
    };

    if (!isRegisteredUser || !authentication?.username) {
        return (
            <LoadPanel
                hideOnOutsideClick={false}
                visible={true}
                message={'Loading...'}
            />
        );
    }

    const onConfirmESign = async (font: Font) => {
        set_viewModalOpen(false);
        const model = {
            noticeId: userNotice.id,
            fontId: parseInt(font.id),
        };
        await signUserNotice(model);
        await loadNotices();
        GenericAlertPopup({
            title: 'Success',
            children:
                'Your form was signed successfully. A regenerated document will be available for download in a few minutes.',
            width: '350px',
        });
        setLoadError(undefined);
    };

    const onFloodReportSign = async (fileToUpload) => {
        set_floodReportSignOpen(false);

        if (fileToUpload) {
            const result = await uploadSignedFloodReport(userNotice.id, fileToUpload);
            if (result) {
                await loadNotices();
                GenericAlertPopup({
                    title: 'Success',
                    children: 'Your document was submitted successfully.',
                    width: '350px',
                });
                setLoadError(undefined);
            } else {
                GenericAlertPopup({
                    title: 'Error',
                    children: 'An error has occurred while communicating with our server.  Please try your operation again.',
                    width: '350px',
                });
                setLoadError(undefined);
            }
        }
    };

    const onCloseESign = async () => {
        set_viewModalOpen(false);
        if (!userNotice.isRead) {
            await setUserNoticeRead(userNotice.id);
            await loadNotices();
        }
    };

    const deleteUserNotice = async (id: number) => await Store.CrossStore.deleteUserNotice(id);

    const deleteNoticeAndReload = async (noticeId) => {
        await deleteUserNotice(noticeId);
        await loadNotices();
    };

    return (
        <div>
            {isMobile ? (
                <MobileNotices
                    notices={notices}
                    onClickViewNotice={onClickViewNotice}
                    deleteNotice={deleteNoticeAndReload}
                />
            ) : (
                <Notices
                    notices={notices}
                    onClickViewNotice={onClickViewNotice}
                    loadingOpen={loadingOpen}
                    markAsRead={markAsRead}
                    deleteNotice={deleteNoticeAndReload}
                    defaultPageSize={defaultPageSize}
                    allowedPageSizes={allowedPageSizes}
                />
            )}

            <EsignNoticeModal
                notice={userNotice}
                noticeType={mapType(userNotice?.noticeTypeId)}
                isOpen={viewModalOpen}
                signatureText={userNotice?.eSignName ?? authentication.user.firstName + ' ' + authentication.user.lastName}
                onCloseCallback={onCloseESign}
                onSignCallback={onConfirmESign}
            />
            <FloodReportEsignModal
                notice={userNotice}
                isOpen={floodReportSignOpen}
                onCloseCallback={onFloodReportSign}
            />
            <LoadPanel
                hideOnOutsideClick={false}
                visible={loadingOpen}
                message={loadingMessage}
            />
        </div>
    );
};

export const mapStateToProps = (state: Store.ApplyRootStateType) => ({
    notices: state.user?.userNotices ?? [],
});

export const mapDispatchToProps = (dispatch: Store.ApplyDispatchType) => {
    const props: { actions: Actions; dispatch: Store.ApplyDispatchType } = {
        actions: {
            getUserNotices: () => dispatch(Store.CrossStore.getUserNotices()),
        },
        dispatch,
    };
    return props;
};

export default connect(mapStateToProps, mapDispatchToProps)(NoticesBase);
